import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { Icon } from '../icon/icon';
import { getTrainingSectionColor } from '../../helpers/training-section-color.helpers';
import { WebSocketService } from '../../services/websocket-service';
import { TrainingModeType } from '../../models/training-mode.model';
import { TrainingSectionType } from '../../models/training-section.model';
import { ApiService } from '../../services/api-service';
import { TranslateModule } from '@ngx-translate/core';
import { CoachingIntervalType } from '../../models/coaching-interval.model';
import { ColorService } from '../../services/color-service';

type ChartSegmentInfoVariant = 'scenic' | 'coaching';

@Component({
  selector: 'app-chart-current-segment-info',
  imports: [Icon, TranslateModule],
  template: `
    <div class="segment-info" [class.coaching]="variant() === 'coaching'">
      @if (variant() === 'coaching') {
        <app-icon icon="instruction" [size]="3" />
        <p class="metric">{{ currentCadence() }} {{ cadenceUnit() | translate }}</p>
        <p class="value percent" [style.background]="currentIntervalColor()">
          {{ currentIntervalPercent() }}%
        </p>
      } @else {
        <app-icon icon="mountain" [size]="3" />
        <p class="label" translate>common.metric.slope</p>
        <p
          class="value slope-value"
          [style.background]="currentSlopeColor()"
          [class.positive]="currentSlope() > 0"
        >
          {{ currentSlope() }}
        </p>
      }
    </div>
  `,
  styleUrl: './chart-current-segment-info.scss',
})
export class ChartCurrentSegmentInfo {
  private readonly webSocketService: WebSocketService = inject(WebSocketService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly colorService: ColorService = inject(ColorService);

  variant: InputSignal<ChartSegmentInfoVariant> = input<ChartSegmentInfoVariant>('scenic');

  protected currentSlope: Signal<number> = computed(() => {
    const trainingMode: TrainingModeType =
      this.webSocketService.contentInformation()?.trainingMode || 'originalSpeed';
    const trainingSections: TrainingSectionType[] = this.apiService.scenicTrainingSections() ?? [];
    if (trainingSections.length === 0) return 0;

    if (trainingMode === 'originalSpeed') {
      const currentTime: number = this.webSocketService.broadcastData()?.data.time || 0;
      const passedSection: number = trainingSections.filter(
        (section: TrainingSectionType) => section.position < currentTime,
      ).length;
      return (
        Math.round((trainingSections[passedSection - 1] ?? trainingSections[0]).slope * 10) / 10
      );
    }

    const currentDistance: number = this.webSocketService.broadcastData()?.data.distance || 0;
    const passedSection: number = trainingSections.filter(
      (trainingSection: TrainingSectionType) => trainingSection.distance <= currentDistance,
    ).length;
    return Math.round((trainingSections[passedSection - 1] ?? trainingSections[0]).slope * 10) / 10;
  });

  protected readonly currentSlopeColor: Signal<string> = computed(() =>
    getTrainingSectionColor(this.currentSlope()),
  );

  private readonly currentTime: Signal<number> = computed(
    () => this.webSocketService.broadcastData()?.data.time ?? 0,
  );

  protected readonly currentInterval: Signal<CoachingIntervalType | undefined> = computed(() => {
    const intervals: CoachingIntervalType[] = this.apiService.coachingIntervals() ?? [];
    const currentTime: number = this.currentTime();
    let currentInterval: CoachingIntervalType | undefined;
    for (const interval of intervals) {
      if (interval.position > currentTime) break;
      currentInterval = interval;
    }
    return currentInterval;
  });

  protected readonly currentCadence: Signal<number> = computed(
    () => this.currentInterval()?.optional_value ?? 0,
  );

  protected readonly currentIntervalPercent: Signal<number> = computed(
    () => this.currentInterval()?.value ?? 0,
  );

  protected readonly currentIntervalColor: Signal<string> = computed(() =>
    this.colorService.getColor(this.currentIntervalPercent()),
  );

  protected readonly cadenceUnit: Signal<string> = computed(() => {
    const sport: string = this.apiService.scenicInformation()?.sport || 'cycling';
    return sport === 'running' ? 'km/h' : `common.metric.cadence.${sport}`;
  });
}
